<template>
  <div class="reset-password-confirm">
    <div class="reset-pwd-title">
      <h2>{{ $t("resetPwd.title") }}</h2>
      <p>{{ $t("resetPwd.subtitle") }}</p>
    </div>
    <div class="reset-pwd-email">
      <a-form layout="vertical" :model="formState" @finish="handleSubmit">
        <a-form-item
          name="password"
          style="margin-bottom: 0"
          :rules="[{ required: true, message: $t('resetPwd.passwordRequired') }]"
        >
          <template #label>
            <label class="form-label"
              >{{ $t("resetPwd.password") }}<span class="required">*</span></label
            >
          </template>
          <a-input-password
            v-model:value="formState.password"
            :class="['custom-input-style', { 'error-input': errors.password.length > 0 }]"
            :disabled="loading"
            @input="clearPasswordErrors"
          />
          <div v-if="errors.password.length > 0" class="error-messages">
            <div v-for="(error, index) in errors.password" :key="index" class="error-message">
              {{ error }}
            </div>
          </div>
        </a-form-item>

        <a-form-item
          name="confirmPassword"
          :rules="[
            { required: true, message: $t('resetPwd.confirmPasswordRequired') },
            { validator: validateConfirmPassword },
          ]"
        >
          <template #label>
            <label class="form-label"
              >{{ $t("resetPwd.confirmPassword") }}<span class="required">*</span></label
            >
          </template>
          <a-input-password
            v-model:value="formState.confirmPassword"
            :class="['custom-input-style', { 'error-input': errors.confirmPassword.length > 0 }]"
            :disabled="loading"
            @input="clearConfirmPasswordErrors"
          />
          <div v-if="errors.confirmPassword.length > 0" class="error-messages">
            <div v-for="(error, index) in errors.confirmPassword" :key="index" class="error-message">
              {{ error }}
            </div>
          </div>
        </a-form-item>

        <div class="reset-pwd-btn">
          <router-link
            to="/sign-in"
            class="back-to-login"
            :class="{ disabled: loading }"
          >
            {{ $t("resetPwd.backToLogin") }}
          </router-link>
          <a-button
            type="primary"
            html-type="submit"
            class="send-pwd-reset-btn"
            :loading="loading"
            :disabled="loading"
          >
            <span>{{ loading ? $t("resetPwd.submittingBtn") : $t("resetPwd.submitBtn") }}</span>
          </a-button>
        </div>
      </a-form>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive } from "vue";
import { onMounted } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useI18n } from "vue-i18n";
import { message } from "ant-design-vue";
import { authAPI } from "../services/api";

const route = useRoute();
const router = useRouter();
const { t } = useI18n();
const loading = ref(false);

const formState = reactive({
  password: "",
  confirmPassword: "",
});

const errors = reactive({
  password: [],
  confirmPassword: [],
});

const clearPasswordErrors = () => {
  errors.password = [];
};

const clearConfirmPasswordErrors = () => {
  errors.confirmPassword = [];
};

const validateConfirmPassword = async (rule, value) => {
  if (value && value !== formState.password) {
    throw new Error(t("resetPwd.passwordsNotMatch"));
  }
};

const handleSubmit = async () => {
  const token = route.query.token;
  const email = route.query.email;
  if (!token || !email) {
    message.error(t("resetPwd.invalidLink"));
    router.push("/");
    return;
  }

  loading.value = true;
  try {
    await authAPI.resetPassword({
      token,
      email,
      password: formState.password,
      password_confirmation: formState.confirmPassword,
    });
    message.success(t("resetPwd.successToast"));
    router.push("/reset-success");
  } catch (error) {
    errors.password = [];
    errors.confirmPassword = [];
    
    const responseData = error?.response?.data;
    if (responseData) {
      if (responseData.content?.errors) {
        const validationErrors = responseData.content.errors;
        if (validationErrors.password) {
          errors.password = validationErrors.password;
        }
        if (validationErrors.password_confirmation) {
          errors.confirmPassword = validationErrors.password_confirmation;
        }
      }
      const msg = responseData.message || t("resetPwd.failedToast");
      message.error(msg);
    } else {
      message.error(t("resetPwd.failedToast"));
    }
  } finally {
    loading.value = false;
  }
};

onMounted(() => {
  // 後端無獨立 token 驗證端點，只確認 URL 帶有必要參數
  const token = route.query.token;
  const email = route.query.email;
  if (!token || !email) {
    message.error(t("resetPwd.invalidLink"));
    router.push("/");
  }
});
</script>

<style scoped>
.reset-password-confirm {
  width: 100%;
  max-width: 550px;
  height: 502px;
  display: flex;
  flex-direction: column;
  align-items: center;
  background: #ffffff;
  border-radius: 20px;
  padding: 40px 32px 30px;
  box-sizing: border-box;
}

.reset-pwd-title {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.reset-pwd-title h2 {
  font-size: 30px;
  font-weight: 900;
  color: #000000;
  margin-bottom: 10px;
}

.reset-pwd-title p {
  padding: 0;
  margin: 0;
  color: #797979;
  font-size: 18px;
  text-align: center;
}

.reset-pwd-email {
  width: 100%;
  margin-top: 40px;
  flex: 1;
}

.form-label {
  font-size: 16px !important;
  font-weight: 400;
  color: #000;
}

.required {
  color: #ff4d4f;
  margin-left: 2px;
}

.custom-input-style {
  height: 64px;
  border-radius: 20px;
  padding: 0 16px;
  font-size: 16px;
  color: #000;
  width: 100%;
  box-sizing: border-box;
}

.error-input {
  border-color: #ff4d4f !important;
  box-shadow: 0 0 0 2px rgba(255, 77, 79, 0.2) !important;
}

.error-messages {
  margin-top: 8px;
  margin-bottom: 16px;
}

.error-message {
  color: #ff4d4f;
  font-size: 14px;
  line-height: 1.4;
  margin-bottom: 4px;
}

.reset-pwd-btn {
  display: flex;
  width: 100%;
  justify-content: flex-end;
  align-items: center;
  gap: 24px;
  margin-top: auto;
}

.send-pwd-reset-btn {
  background: var(--color-primary);
  border-radius: 40px;
  height: 50px;
  padding: 0 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #ffffff;
  font-size: 16px;
  text-decoration: none;
  border: none;
}

.send-pwd-reset-btn:disabled {
  background: #ccc;
  cursor: not-allowed;
}

.back-to-login {
  font-size: 16px;
  font-weight: 500;
  color: var(--color-primary);
  background: transparent;
  text-decoration: none;
}

.back-to-login:hover {
  color: var(--color-primary-hover);
}

.back-to-login.disabled {
  color: #ccc;
  cursor: not-allowed;
  pointer-events: none;
}

/* ✅ 手機版樣式 */
@media (max-width: 768px) {
  .reset-password-confirm {
    padding: 32px 20px 24px;
    height: auto !important;
  }

  .reset-pwd-title h2 {
    font-size: 24px;
  }

  .reset-pwd-title p {
    font-size: 14px;
    line-height: 1.4;
  }

  .custom-input-style {
    height: 56px;
    font-size: 15px;
  }

  .reset-pwd-email {
    margin-top: 32px;
  }

  .reset-pwd-btn {
    flex-direction: column;
    align-items: stretch;
    gap: 16px;
    margin-top: 32px;
  }

  .send-pwd-reset-btn,
  .back-to-login {
    width: 100%;
    text-align: center;
  }

  .send-pwd-reset-btn {
    height: 48px;
    font-size: 15px;
  }

  .error-message {
    font-size: 13px;
  }
}
</style>
